import { ReactComponent as ChevronDown } from '@basqet-dashboard/common/assets/icons/arrow-down.svg';
import { Coin } from '@basqet-dashboard/common/components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

// Figma node 13092:83140 shows the open menu inline below the trigger —
// it pushes the rest of the form down rather than floating over it. The
// shared <Dropdown> renders an absolutely-positioned popup, so this is a
// purpose-built component for the conversion flow.

const fixedBalance = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00000000';
  return n.toFixed(8);
};

const WalletSelect = ({ value, options, placeholder, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = options.find((option) => option.value === value);
  // Trigger shows the slug only (BTC / NGN / ETH) per figma node
  // 13092:80035; the full currency name lives in the open menu rows.
  const triggerLabel = selected ? String(selected.value).toUpperCase() : placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Native <select> mirror keeps existing tests (which use
  // fireEvent.change on the aria-labelled field) working without churn.
  // It sits visually-hidden over the trigger and pipes the same onChange.
  const handleNativeChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <div ref={wrapperRef} className={classNames('wallet-select', { 'wallet-select--open': open })}>
      <button
        type="button"
        className={classNames('wallet-select__trigger', {
          'wallet-select__trigger--placeholder': !selected
        })}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}>
        {selected && (
          <Coin
            slug={selected.value}
            icon_url={selected.iconUrl}
            className="wallet-select__trigger-icon coin__icon"
          />
        )}
        <span className="wallet-select__trigger-label">{triggerLabel}</span>
        <ChevronDown className="wallet-select__trigger-chevron" aria-hidden />
      </button>

      <select
        className="wallet-select__native"
        aria-label={placeholder}
        value={value}
        onChange={handleNativeChange}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {String(option.value).toUpperCase()}
            {option.name ? ` — ${option.name}` : ''}
          </option>
        ))}
      </select>

      {open && (
        <ul className="wallet-select__menu" role="listbox" aria-label={placeholder}>
          {options.map((option) => {
            const slug = String(option.value).toUpperCase();
            const isSelected = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={classNames('wallet-select__option', {
                    'wallet-select__option--selected': isSelected
                  })}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}>
                  <Coin
                    slug={option.value}
                    icon_url={option.iconUrl}
                    className="wallet-select__option-icon coin__icon"
                  />
                  <span className="wallet-select__option-text">
                    <span className="wallet-select__option-name">{option.name || slug}</span>
                    {Number.isFinite(option.balance) && (
                      <span className="wallet-select__option-balance">
                        {fixedBalance(option.balance)} {slug}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

WalletSelect.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string,
      name: PropTypes.string,
      balance: PropTypes.number,
      iconUrl: PropTypes.string
    })
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export { WalletSelect };
